import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';

export interface ErrorModalData {
  errorCode: string;
  title: string;
  subMessage: string;
  buttonText: string;
  showSubMessage: boolean;
}

@Component({
  selector: 'app-error-modal',
  template: `
    <div class="modal fade" id="errorModal" tabindex="-1" aria-labelledby="errorModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered send-quote-modal">
        <div class="modal-content">
          <div class="modal-body">
            <button type="button" class="btn-close" (click)="hideModal()" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                <path d="M14.4995 16.1911L8.57865 22.112C8.35712 22.3335 8.07517 22.4443 7.73281 22.4443C7.39045 22.4443 7.10851 22.3335 6.88698 22.112C6.66545 21.8905 6.55469 21.6085 6.55469 21.2661C6.55469 20.9238 6.66545 20.6418 6.88698 20.4203L12.8078 14.4995L6.88698 8.57865C6.66545 8.35712 6.55469 8.07517 6.55469 7.73281C6.55469 7.39045 6.66545 7.10851 6.88698 6.88698C7.10851 6.66545 7.39045 6.55469 7.73281 6.55469C8.07517 6.55469 8.35712 6.66545 8.57865 6.88698L14.4995 12.8078L20.4203 6.88698C20.6418 6.66545 20.9238 6.55469 21.2661 6.55469C21.6085 6.55469 21.8905 6.66545 22.112 6.88698C22.3335 7.10851 22.4443 7.39045 22.4443 7.73281C22.4443 8.07517 22.3335 8.35712 22.112 8.57865L16.1911 14.4995L22.112 20.4203C22.3335 20.6418 22.4443 20.9238 22.4443 21.2661C22.4443 21.6085 22.3335 21.8905 22.112 22.112C21.8905 22.3335 21.6085 22.4443 21.2661 22.4443C20.9238 22.4443 20.6418 22.3335 20.4203 22.112L14.4995 16.1911Z" fill="#2828E0"/>
              </svg>
            </button>
            
            <h5 class="modal-title" id="errorModalLabel">{{ errorData?.title || 'Error' }}</h5>
            
            <p class="no-bg" style="display: block;" *ngIf="errorData?.showSubMessage">Si el problema continúa, contáctanos en &nbsp; <a href="mailto:service&#64;blueassist.biz" target="_blank">service&#64;blueassist.biz</a>.</p>
            
            <button type="button" class="btn btn-primary send-email-button" (click)="onActionClick()">
              {{ errorData?.buttonText || 'OK' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .btn-close {
      position: absolute;
      top: 15px;
      right: 15px;
      background: none;
      border: none;
      cursor: pointer;
    }
    
    .modal-title {
      color: #333;
      font-weight: 600;
      margin-bottom: 20px;
      text-align: center;
    }
    
    .no-bg {
      color: #666;
      margin-bottom: 25px;
      text-align: center;
      line-height: 1.5;
    }
    
    .send-email-button {
      width: 100%;
      padding: 12px 24px;
      font-weight: 500;
    }
  `]
})
export class ErrorModalComponent implements OnInit, OnDestroy {
  @Input() errorData: ErrorModalData;
  @Output() actionClick = new EventEmitter<string>();
  
  private modal: any;

  ngOnInit() {
    console.log('🔍 ErrorModalComponent initialized');
    // Initialize Bootstrap modal after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeModal();
    }, 100);
  }

  ngOnDestroy() {
    try {
      if (this.modal && this.modal._element) {
        this.modal.dispose();
      }
    } catch (e) {
      // Modal already disposed, ignore
    }
  }

  ngOnChanges(changes: any) {
    
    if (changes.errorData && changes.errorData.currentValue) {
      
      // Auto-show modal when data is received
      if (this.errorData) {
        
        // Force modal initialization if not already done
        if (!this.modal) {

          this.initializeModal();
        }
        
        setTimeout(() => this.showModal(), 200);
      }
    } else {
      // console.log('🔍 No errorData changes detected');
    }
  }

  private initializeModal() {
    const modalElement = document.getElementById('errorModal');

    
    if (modalElement && (window as any).bootstrap) {
      try {
        this.modal = new (window as any).bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
      } catch (error) {
        console.error('🔍 Error initializing Bootstrap modal:', error);
      }
    } 
  }

  onActionClick(): void {

    this.actionClick.emit(this.errorData.errorCode);
    this.hideModal();
  }

  showModal(): void {

    if (this.modal) {
      try {
        this.modal.show();

      } catch (error) {
        console.error('🔍 Error showing modal:', error);
        this.showModalFallback();
      }
    } else {
      this.initializeModal();
      if (this.modal) {
        this.modal.show();
      } else {

        this.showModalFallback();
      }
    }
  }

  /**
   * Fallback method to show modal if Bootstrap is not available
   */
  private showModalFallback(): void {

    const modalElement = document.getElementById('errorModal');
    if (modalElement) {
      // Remove fade class and set display block
      modalElement.classList.remove('fade');
      modalElement.style.display = 'block';
      modalElement.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      modalElement.setAttribute('aria-hidden', 'false');

    } else {
      console.error('🔍 Modal element not found for fallback');
    }
  }

  hideModal(): void {

    if (this.modal) {
      try {
        this.modal.hide();
      } catch (error) {
        console.error('🔍 Error hiding modal:', error);
        this.hideModalFallback();
      }
    } else {
      this.hideModalFallback();
    }
  }

  /**
   * Fallback method to hide modal if Bootstrap is not available
   */
  private hideModalFallback(): void {

    const modalElement = document.getElementById('errorModal');
    if (modalElement) {
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');

    }
  }
}
